import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import TitleComponent from "../components/TitleComponent/TitleComponent";
import TableComponent from "../components/TableComponent/TableComponent";
import DashboardCards from "../components/DashboardCards/DashboardCards";
import UserComponent from "../components/UserComponent/UserComponent";
import { ICONS } from "../constants/constants";
import { handleGetCompanyData } from "../store/actions/adminActions";
import Loader from "../assets/images/loader.gif";
import { Nav } from "react-bootstrap";
import EditProfileForm from "../components/profileSetting/superAdmin/EditProfileForm";
import { toast } from "react-toastify";
import Cookies from "js-cookie";

const Manager = () => {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { adminList, isLoading } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.user);

  const manager = location.state?.manager;
  const managerId = id || manager?.id;

  const lastCompanyRef = useRef(null);

  useEffect(() => {
    // determine company id from manager state or adminList once
    const companyId = manager?.company?.id || adminList?.admin?.company?.id;
    if (companyId && lastCompanyRef.current !== companyId) {
      lastCompanyRef.current = companyId;
      dispatch(handleGetCompanyData(companyId));
    }
  }, [dispatch, manager?.company?.id, adminList?.admin?.company?.id]);

  const companySource = adminList?.admin || adminList?.data?.company;

  const users = companySource?.adminUsers || companySource?.suppliers || [];

  // cards (reuse DashboardCards style)
  const cardData = [
    { title: "No of Farmers", value: `${users.filter((u) => u.role === "farmer").length || 0}`, colorKey: "farmer", imageKey: "farmerIcon" },
    { title: "No of Suppliers", value: `${users.filter((u) => u.role === "supplier").length || 0}`, colorKey: "supplier", imageKey: "supplierIcon" },
    { title: "No of Consultants", value: `${users.filter((u) => u.role === "consultant").length || 0}`, colorKey: "consultant", imageKey: "consultantIcon" },
  ];

  const farmers = users.filter((u) => u.role === "farmer");
  const suppliers = users.filter((u) => u.role === "supplier");
  const consultants = users.filter((u) => u.role === "consultant");

  const tableTitle = "Users";

  // default show farmers
  const [selectedType, setSelectedType] = useState("farmer");
  const [isEditing, setIsEditing] = useState(false);

  const buildRow = (u) => ({
    Name: u?.name,
    Company: companySource?.name || u?.company?.name || "N/A",
    Mobile: u?.mobile_number || "N/A",
    CNIC: u?.cnic || "N/A",
    Status: u?.account_status || "N/A",
    Actions: (
      <>
        <a href={`/${u.role}/${u.id}`} rel="noopener noreferrer">
          <img src={ICONS.viewIcon} alt="View" className="tick-icon" />
        </a>
        <img src={ICONS.downloadIcon} alt="Download" className="cross-icon" style={{ opacity: 0.5, pointerEvents: "none" }} />
      </>
    ),
  });

  let data = [];
  if (selectedType === "farmer") data = farmers.filter(f => f.account_status === "Approved").map(buildRow);
  if (selectedType === "supplier") data = suppliers.filter(s => s.account_status === "Approved").map(buildRow);
  if (selectedType === "consultant") data = consultants.filter(c => c.account_status === "Approved").map(buildRow);

  if (isLoading) {
    return (
      <div className="loaderOverlay">
        <img src={Loader} alt="Loader" />
      </div>
    );
  }

  return (
    <Container>
      <Row>
        <Col lg={12} className="component-container mb-2">
          <TitleComponent title="Manager" />
          
          {/* Manager profile display */}
          { (manager || users.find((u)=>u.id===managerId)) && (() => {
            const mObj = manager || users.find((u)=>u.id===managerId) || {};
            const userData = {
              id: mObj?.id || "N/A",
              name: mObj?.name || "N/A",
              userdatarole: mObj?.role || "manager",
              email: mObj?.email || "N/A",
              joinedDate: mObj?.created_at || "N/A",
              cnic: mObj?.cnic || "N/A",
              mobile: mObj?.mobile_number || "N/A",
              city: mObj?.city || "N/A",
              address: mObj?.address || "N/A",
              company: mObj?.company?.name || companySource?.name || "N/A",
              account_status: mObj?.account_status || "N/A",
              profile_image: mObj?.profile_image || null,
            };
            const userDetailsArray = [
              { label: "Mobile Number", value: userData.mobile },
              { label: "City", value: userData.city },
              { label: "Address", value: userData.address },
              { label: "Company", value: userData.company || "N/A" },
            ];
            return isEditing ? (
              <EditProfileForm
                user={{
                  name: userData.name,
                  country: mObj?.country || "",
                  city: userData.city,
                  address: userData.address,
                  mobile_number: userData.mobile,
                }}
                onSubmit={async (updatedData) => {
                  try {
                    const res = await fetch(`${process.env.REACT_APP_BASE_URI}/manager/profile/${managerId}`, {
                      method: "PUT",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Cookies.get("auth-token")}`,
                      },
                      body: JSON.stringify(updatedData),
                    });
                    const dataResp = await res.json();
                    if (res.ok && dataResp?.statusCode === 200) {
                      toast.success("Profile updated");
                      setIsEditing(false);
                      const companyId = manager?.company?.id || adminList?.admin?.company?.id;
                      if (companyId) dispatch(handleGetCompanyData(companyId));
                    } else {
                      toast.error(dataResp?.message || "Update failed");
                    }
                  } catch (err) {
                    toast.error(err.message || "Update failed");
                  }
                }}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <UserComponent user={userData} userDetails={userDetailsArray} onEdit={() => setIsEditing(true)} />
            );
          })()}
          {user?.role !== "superadmin" && <DashboardCards cardData={cardData} />}
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <Nav variant="tabs" activeKey={selectedType} onSelect={(k) => setSelectedType(k)}>
            <Nav.Item>
              <Nav.Link eventKey="farmer">Farmers</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="supplier">Suppliers</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="consultant">Consultants</Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <Row>
        <Col lg={12} className="component-container mb-2">
          <TableComponent
            columns={["Name", "Company", "Mobile", "CNIC", "Status", "Actions"]}
            data={data}
            title={tableTitle}
            showSearchBar={true}
            showFilter={false}
            showTabs={false}
            showColors={false}
          />
        </Col>
      </Row>
      {/* inline edit form handled via EditProfileForm when isEditing is true */}
    </Container>
  );
};

export default Manager;

